import { memo, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ViewStyle, StyleProp } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { DialogCard } from "./DialogCard";

interface DialogModalProps {
  visible: boolean;
  onClose: () => void;
  showCloseButton?: boolean;
  title: string;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}

/**
 * Web portals the dialog itself rather than using React Native's `Modal`.
 *
 * `Modal` on react-native-web renders into a bare `<div>` appended to
 * `document.body`, wrapped in a `position: fixed` element with **no z-index**
 * (`react-native-web/dist/exports/Modal/ModalContent.js`). Because
 * `position: fixed` always establishes a stacking context, that wrapper
 * competes at the root as `z-index: auto` — so any page content carrying an
 * explicit positive z-index paints *above* the dialog. The dialog still looked
 * correct (it's painted later in tree order for most content) but hit-testing
 * resolved to the page underneath, so clicking an `Autocomplete` option inside
 * the dialog fell through to the page and navigated away.
 *
 * Portalling here puts `z-index` on the fixed backdrop we own, which is the
 * element that participates in the root stacking context — so it genuinely
 * outranks page-level z-indexes instead of being trapped a level too deep.
 */
export const DialogModal = memo(
  ({ visible, onClose, showCloseButton = true, title, children, style }: DialogModalProps) => {
    const { theme } = useUnistyles();

    useEffect(() => {
      if (!visible) return;
      // Bound to keydown rather than keyup, and skipped once the event is
      // already defaulted, so an inner control that consumed Escape keeps the
      // dialog open. Controls handle Escape on keydown and call preventDefault
      // there (`use-list-keyboard`), and `defaultPrevented` does not carry
      // across to the later keyup event — so a keyup listener saw every Escape
      // as unhandled. In ConfirmDumpModal that meant dismissing an Autocomplete
      // dropdown also closed the dialog and discarded the form.
      //
      // Listening on document in the bubble phase is what makes this work: the
      // inner handler runs first, so `defaultPrevented` is already set by the
      // time this sees the event.
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape" && !event.defaultPrevented) onClose();
      };
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }, [visible, onClose]);

    if (!visible) return null;

    return createPortal(
      <DialogCard
        onClose={onClose}
        showCloseButton={showCloseButton}
        title={title}
        style={style}
        backdropStyle={
          // `position: fixed` isn't in React Native's ViewStyle union, so this
          // web-only style is cast rather than declared via StyleSheet.create.
          {
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: theme.zIndex.modal,
          } as unknown as ViewStyle
        }
      >
        {children}
      </DialogCard>,
      document.body,
    );
  },
);

DialogModal.displayName = "DialogModal";
